/**
 * Serializes work against a shared DuckDB-wasm database, keyed by what it
 * touches.
 *
 * DuckDB-wasm is one database for the whole tab, so two overlapping runs
 * against the same table interleave: a second `CREATE OR REPLACE TABLE` lands
 * between the first run's statement replay and the table it was building
 * disappears, surfacing as "Column with name X already exists" (or a missing
 * table) and a failed preview. Effects re-run — a remount in dev, a changed
 * dependency, a new artifact — so overlapping runs are the norm, not an edge
 * case.
 *
 * Keyed rather than global, so a dataset page and a row picker mounted at once
 * do not wait on each other. Work against *different* tables has never been the
 * problem.
 */
export type DuckDbWorkQueue = {
  /** Queues work behind everything already queued for `key`. */
  run: <T>(key: string, work: () => Promise<T>) => Promise<T>;
  /**
   * Same, but checked once the queued run actually starts, so a superseded run
   * does no work. Use for work that a later run fully replaces — a
   * re-materialization — never for an increment a later run would not redo.
   */
  runIfCurrent: (
    key: string,
    shouldRun: () => boolean,
    work: () => Promise<void>
  ) => Promise<void>;
};

export const createDuckDbWorkQueue = (): DuckDbWorkQueue => {
  const tails = new Map<string, Promise<unknown>>();

  const run = <T>(key: string, work: () => Promise<T>): Promise<T> => {
    const tail = tails.get(key) ?? Promise.resolve();
    // Both handlers, so a rejected predecessor still releases the queue: one
    // error must not wedge every run after it.
    const next = tail.then(work, work);
    const settled = next.then(
      () => undefined,
      () => undefined
    );
    tails.set(key, settled);
    // Drop the key once it drains, so a long-lived tab does not accumulate one
    // resolved promise per table it has ever opened.
    void settled.then(() => {
      if (tails.get(key) === settled) {
        tails.delete(key);
      }
    });
    return next;
  };

  return {
    run,
    runIfCurrent: (key, shouldRun, work) =>
      run(key, async () => {
        if (!shouldRun()) {
          return;
        }
        await work();
      }),
  };
};
